import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { useMemo } from "react";
import {
  AddRegular,
  BuildingRegular,
  CalendarLtrRegular,
  ChatRegular,
  ClockRegular,
  CubeRegular,
  DataPieRegular,
  DocumentRegular,
  GaugeRegular,
  GridRegular,
  LinkRegular,
  MailRegular,
  PeopleRegular,
  PlugConnectedRegular,
  StackRegular,
  StarRegular,
} from "@fluentui/react-icons";
import { WORKSPACE_TAB_TITLES } from "apps/project-iq/constants/workspaceTabs";
import { useQualityReviewAccess } from "apps/project-iq/pages/QualityReviewPage/useQualityReviewAccess";
import { PROJECT_IQ_CLIENT_APP_BASE } from "config";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { useStyles } from "./WorkspaceNav.styles";
import { IDeliveryNavItem } from "./WorkspaceNav.types";

interface IWorkspaceNavProps {
  workspace: IProjectIQWorkspace;
  activeTab: string;
  onTabSelect: (tab: string) => void;
  onAddCapability: () => void;
}

const CAPABILITY_ICONS: Record<string, React.FC<{ className?: string }>> = {
  capabilities: PlugConnectedRegular,
  diagnostics: GaugeRegular,
  "nh-diagnostic-iq": GaugeRegular,
  "ai-analytics": DataPieRegular,
};

const PINNED_DELIVERY_CAPS = new Set<string>([
  "nh-diagnostic-iq",
  "ai-analytics",
  "diagnostics",
]);
const INTERNAL_VIEW_CAPS = new Set(["chat", "files", "vault"]);
const MOBILIZATION_CAPS = new Set(["people", "value"]);
const COMING_SOON_CAPS = new Set<string>([]);
const DEFAULT_DELIVERY_CAPS = ["diagnostics"];

// The two client view tabs are pages of the server-rendered client app, not tabs
// of this one, so they are links out rather than `onTabSelect` calls. The nav id
// on the left is what this app calls them; the value is the `tab` the client app
// expects.
const CLIENT_VIEW_TAB_QUERY: Record<string, string> = {
  "client-overview": "overview",
  "client-deliverables": "deliverables",
};

const isSafeUrl = (url: string) => /^https?:\/\//i.test(url);

/**
 * The client app's page for one workspace with a tab already selected.
 *
 * The client app addresses a workspace by an opaque reference it derives from a
 * server side secret, so the link goes through its `by-id` route and lets it do
 * that translation. The base already resolves to port 8000 on the dev server and
 * to the current origin everywhere else, so the same call works in both.
 */
const clientViewUrl = (workspaceId: string, tab: string): string =>
  `${PROJECT_IQ_CLIENT_APP_BASE}/workspaces/by-id/${encodeURIComponent(workspaceId)}/?tab=${encodeURIComponent(tab)}`;

export const WorkspaceNav = ({
  workspace,
  activeTab,
  onTabSelect,
  onAddCapability,
}: IWorkspaceNavProps): JSX.Element => {
  const styles = useStyles();
  const canAccessQualityReview = useQualityReviewAccess(workspace.id);

  const capabilityBySlug = useMemo(
    () => new Map(workspace.capabilities?.map((cap) => [cap.slug, cap]) ?? []),
    [workspace.capabilities],
  );

  const deliveryCaps = useMemo(() => {
    const slugs = workspace.capabilities?.length
      ? workspace.capabilities.map((cap) => cap.slug)
      : DEFAULT_DELIVERY_CAPS;
    return slugs.filter(
      (slug) =>
        !INTERNAL_VIEW_CAPS.has(slug) &&
        !MOBILIZATION_CAPS.has(slug) &&
        !COMING_SOON_CAPS.has(slug) &&
        !PINNED_DELIVERY_CAPS.has(slug) &&
        (slug in WORKSPACE_TAB_TITLES || !!capabilityBySlug.get(slug)?.url),
    );
  }, [workspace.capabilities, capabilityBySlug]);

  const sortedDeliveryItems = useMemo<IDeliveryNavItem[]>(() => {
    const items: IDeliveryNavItem[] = [
      ...deliveryCaps.map((slug) => {
        const resolved = capabilityBySlug.get(slug);
        const rawUrl = resolved?.url;
        const url = rawUrl && isSafeUrl(rawUrl) ? rawUrl : undefined;
        return {
          id: slug,
          label: WORKSPACE_TAB_TITLES[slug] ?? resolved?.title ?? slug,
          url,
          description: resolved?.shortDescription,
        };
      }),
      ...(workspace.customCapabilities ?? [])
        .filter((cap) => cap.isEnabled !== false)
        .map((cap) => ({
          id: cap.id,
          label: cap.name,
          url: isSafeUrl(cap.url) ? cap.url : undefined,
          description: cap.description,
        })),
    ];
    return items.sort((first, second) =>
      first.label.localeCompare(second.label),
    );
  }, [deliveryCaps, capabilityBySlug, workspace.customCapabilities]);

  const nav = (
    id: string,
    label: string,
    Icon: React.FC<{ className?: string }>,
    description?: string,
  ): JSX.Element => {
    const button = (
      <button
        key={id}
        type="button"
        className={mergeClasses(
          styles.navItem,
          activeTab === id ? styles.navItemActive : undefined,
        )}
        onClick={() => onTabSelect(id)}
        aria-label={label}
        aria-current={activeTab === id ? "page" : undefined}
      >
        <span
          className={mergeClasses(
            styles.navIconContainer,
            activeTab === id ? styles.navIconActive : styles.navIconInactive,
          )}
          aria-hidden="true"
        >
          <Icon className={styles.navIcon} />
        </span>
        <span className={styles.navLabel}>{label}</span>
      </button>
    );
    if (!description) return button;
    return (
      <Tooltip
        key={id}
        content={description}
        relationship="description"
        withArrow
      >
        {button}
      </Tooltip>
    );
  };

  const externalNav = (
    id: string,
    label: string,
    Icon: React.FC<{ className?: string }>,
    url: string,
    description?: string,
  ): JSX.Element => {
    const button = (
      <button
        key={id}
        type="button"
        className={styles.navItem}
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        aria-label={`${label} (opens in new tab)`}
      >
        <span
          className={mergeClasses(
            styles.navIconContainer,
            styles.navIconInactive,
          )}
          aria-hidden="true"
        >
          <Icon className={styles.navIcon} />
        </span>
        <span className={styles.navLabel}>{label}</span>
      </button>
    );
    if (!description) return button;
    return (
      <Tooltip
        key={id}
        content={description}
        relationship="description"
        withArrow
      >
        {button}
      </Tooltip>
    );
  };

  return (
    <nav className={styles.panel} aria-label="Workspace navigation">
      <div className={styles.workspaceHeader}>
        <div className={styles.clientRow}>
          <BuildingRegular className={styles.buildingIcon} />
          <span className={styles.clientName}>{workspace.clientName}</span>
        </div>
        <div className={styles.workspaceName}>{workspace.name}</div>
        {workspace.code && (
          <div className={styles.codeLine}>
            <span className={styles.codeBadge}>{workspace.code}</span>
          </div>
        )}
      </div>

      <div className={styles.navContent}>
        {/*   TODO: Uncomment when the client view is restored.*/}
        {/* <div
          className={mergeClasses(
            styles.sectionHeader,
            styles.sectionHeaderFirst,
          )}
        >
          Internal View
        </div> */}

        {nav("overview", WORKSPACE_TAB_TITLES.overview, GridRegular)}
        {nav("files", WORKSPACE_TAB_TITLES.files, StackRegular)}
        {nav("vault", WORKSPACE_TAB_TITLES.vault, CubeRegular)}
        {nav("chat", WORKSPACE_TAB_TITLES.chat, ChatRegular)}

        <div role="group" aria-labelledby="nav-group-mobilization">
          <span id="nav-group-mobilization" className={styles.groupLabel}>
            Mobilization
          </span>
          {nav("people", WORKSPACE_TAB_TITLES.people, PeopleRegular)}
          {nav("value", WORKSPACE_TAB_TITLES.value, StarRegular)}
        </div>

        <div role="group" aria-labelledby="nav-group-delivery">
          <div className={styles.deliveryGroupHeader}>
            <span id="nav-group-delivery" className={styles.deliveryGroupLabel}>
              Delivery
            </span>
            <button
              type="button"
              className={styles.addCapabilityButton}
              onClick={onAddCapability}
              aria-label="Add capability"
            >
              <AddRegular aria-hidden="true" />
            </button>
          </div>
          {nav("plan", WORKSPACE_TAB_TITLES.plan, CalendarLtrRegular)}
          {nav(
            "nh-diagnostic-iq",
            WORKSPACE_TAB_TITLES["nh-diagnostic-iq"],
            GaugeRegular,
            capabilityBySlug.get("nh-diagnostic-iq")?.shortDescription,
          )}
          {capabilityBySlug.get("ai-analytics")?.enabled &&
            nav(
              "ai-analytics",
              WORKSPACE_TAB_TITLES["ai-analytics"],
              DataPieRegular,
              capabilityBySlug.get("ai-analytics")?.shortDescription,
            )}
          {sortedDeliveryItems.map((item) =>
            item.url
              ? externalNav(
                  item.id,
                  item.label,
                  LinkRegular,
                  item.url,
                  item.description,
                )
              : nav(
                  item.id,
                  item.label,
                  CAPABILITY_ICONS[item.id] ?? LinkRegular,
                  item.description,
                ),
          )}
        </div>

        <div role="group" aria-labelledby="nav-group-closing">
          <span id="nav-group-closing" className={styles.groupLabel}>
            Closing
          </span>
          {nav(
            "value-story",
            WORKSPACE_TAB_TITLES["value-story"],
            DocumentRegular,
          )}
          {nav(
            "retrospective",
            WORKSPACE_TAB_TITLES["retrospective"],
            ClockRegular,
          )}
          {canAccessQualityReview &&
            nav(
              "quality-review",
              WORKSPACE_TAB_TITLES["quality-review"],
              LinkRegular,
            )}
          {nav(
            "project-reviews",
            WORKSPACE_TAB_TITLES["project-reviews"],
            GridRegular,
          )}
        </div>

        {/*   TODO: Uncomment when the client view is restored.*/}
        {/* <div className={styles.sectionHeader}>Client View</div> */}
        {/* {externalNav(
          "client-overview",
          WORKSPACE_TAB_TITLES["client-overview"],
          GridRegular,
          clientViewUrl(workspace.id, CLIENT_VIEW_TAB_QUERY["client-overview"]),
        )} */}
        {/* {externalNav(
          "client-deliverables",
          WORKSPACE_TAB_TITLES["client-deliverables"],
          DocumentRegular,
          clientViewUrl(
            workspace.id,
            CLIENT_VIEW_TAB_QUERY["client-deliverables"],
          ),
        )} */}
        {/* {nav(
          "client-invitations",
          WORKSPACE_TAB_TITLES["client-invitations"],
          MailRegular,
        )} */}
      </div>
    </nav>
  );
};
